// File: src/pages/Inventory/BinsAndLocations.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { FaSyncAlt } from "react-icons/fa";
import api from "@/lib/axios";

/* ───────────────────────────── helpers ───────────────────────────── */
const toStr = (v) => (v == null ? "" : String(v));
const toLabel = (v) => toStr(v).trim();

function useDebounced(value, delayMs = 250) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

function normalizeLocations(data) {
  const rows = Array.isArray(data)
    ? data
    : Array.isArray(data?.items)
      ? data.items
      : Array.isArray(data?.rows)
        ? data.rows
        : [];

  return rows
    .map((r) => {
      const name = toStr(r?.name ?? r?.location_name ?? r?.location ?? "").trim();
      if (!name) return null;
      return {
        location_id: r?.location_id ?? r?.id ?? null,
        name,
        is_default_goods_received: r?.is_default_goods_received === true,
        is_default_wastage: r?.is_default_wastage === true || r?.is_default_waste === true,
      };
    })
    .filter(Boolean);
}

/* ───────────────────────────── modal ───────────────────────────── */
function NewMovementModal({ open, onClose, locations, onSaved }) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const [type, setType] = useState("RECEIVE");

  // item picker
  const [itemQuery, setItemQuery] = useState("");
  const dItemQuery = useDebounced(itemQuery, 300);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggest, setShowSuggest] = useState(false);
  const [pickedItem, setPickedItem] = useState(null);

  // qty & locations
  const [qty, setQty] = useState("");
  const [fromLoc, setFromLoc] = useState("");
  const [toLoc, setToLoc] = useState("");
  const [reference, setReference] = useState("");

  // optional bin selectors
  const [fromBin, setFromBin] = useState("");
  const [toBin, setToBin] = useState("");
  const [bins, setBins] = useState([]); // directory rows
  const [binsLoadErr, setBinsLoadErr] = useState("");

  // date/time (local)
  const [txDate, setTxDate] = useState(() => {
    const d = new Date();
    d.setSeconds(0, 0);
    return new Date(d.getTime() - d.getTimezoneOffset() * 60000)
      .toISOString()
      .slice(0, 16);
  });

  // on-hand by location (hint only)
  const [onHandByLoc, setOnHandByLoc] = useState({});
  const suggBoxRef = useRef(null);

  // default GR location name
  const goodsReceivedName = useMemo(() => {
    const byFlag = (locations || []).find((l) => l?.is_default_goods_received);
    if (byFlag && toStr(byFlag.name)) return toStr(byFlag.name);
    const byName = (locations || []).find((l) => toStr(l?.name).toLowerCase() === "goods received");
    return byName ? toStr(byName.name) : "";
  }, [locations]);

  // reset on open
  useEffect(() => {
    if (!open) return;
    setSubmitting(false);
    setError("");
    setType("RECEIVE");
    setItemQuery("");
    setSuggestions([]);
    setShowSuggest(false);
    setPickedItem(null);
    setQty("");
    setFromLoc("");
    setToLoc(goodsReceivedName || "");
    setReference("");
    setFromBin("");
    setToBin("");
    const d = new Date();
    d.setSeconds(0, 0);
    setTxDate(new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16));
    setOnHandByLoc({});
  }, [open, goodsReceivedName]);

  // load bin directory (for autocomplete)
  useEffect(() => {
    if (!open) return;
    (async () => {
      setBinsLoadErr("");
      const endpoints = ["/inventory/bin-directory", "/inventory/inventory-bins", "/inventory/bins"];
      let lastErr;
      for (const url of endpoints) {
        try {
          const { data } = await api.get(url);
          const list = Array.isArray(data)
            ? data
            : Array.isArray(data?.items)
              ? data.items
              : Array.isArray(data?.rows)
                ? data.rows
                : Array.isArray(data?.data)
                  ? data.data
                  : [];
          const norm = list.map((r) => {
            const code = toStr(r?.bin_code ?? r?.code ?? r?.name ?? "");
            const name = toStr(r?.name ?? r?.bin_name ?? r?.description ?? code);
            const loc = toStr(r?.location_name ?? r?.location ?? r?.site ?? "");
            const type = toStr(r?.bin_type ?? r?.type ?? "").toLowerCase();
            const nlc = name.toLowerCase();
            const isGR = r?.is_goods_received === true || /goods\s*received|^gr$|^grn$/.test(nlc);
            const isWaste =
              r?.is_wastage === true ||
              r?.is_waste === true ||
              /waste|wastage|expired|damaged/.test(nlc);

            return {
              bin_id: r?.bin_id ?? r?.id ?? null,
              bin_code: code,
              name,
              location_name: loc,
              bin_type: type || (isGR ? "goods_received" : isWaste ? "waste" : "normal"),
              is_goods_received: !!isGR,
              is_wastage: !!isWaste,
            };
          });
          setBins(norm);
          lastErr = null;
          break;
        } catch (e) {
          lastErr = e;
          if (e?.response?.status !== 404) break;
        }
      }
      if (lastErr) {
        setBins([]);
        setBinsLoadErr("Bin directory endpoint not found. Bin autocomplete will be disabled.");
      }
    })();
  }, [open]);

  // item suggestions
  useEffect(() => {
    let cancel = false;
    (async () => {
      if (!open) return;
      const q = toStr(dItemQuery).trim();
      if (!q) {
        if (!cancel) setSuggestions([]);
        return;
      }
      try {
        const { data } = await api.get("/inventory/stock-levels", {
          params: { q, limit: 10, sort: "item_code", dir: "asc" },
        });
        const list = (Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : []).map((r) => ({
          item_id: r?.item_id ?? r?.stock_item_id ?? r?.id,
          item_code: toStr(r?.item_code ?? r?.code ?? ""),
          description: toStr(r?.description ?? r?.stock_name ?? ""),
          avg_cost: r?.avg_cost ?? r?.cost ?? r?.unit_price ?? null,
        }));
        if (!cancel) setSuggestions(list);
      } catch {
        if (!cancel) setSuggestions([]);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [dItemQuery, open]);

  async function refreshOnHandHints(code) {
    try {
      const c = toStr(code);
      if (!c) return setOnHandByLoc({});
      const { data } = await api.get("/inventory/bin-stock", { params: { item_code: c } });
      const rows = Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : [];
      const map = {};
      rows.forEach((r) => {
        const loc = toStr(r?.location_name ?? r?.location ?? r?.bin ?? "Unspecified");
        const qty = Number(r?.qty ?? r?.quantity ?? r?.on_hand ?? r?.on_hand_qty ?? 0);
        map[loc] = (map[loc] || 0) + (Number.isFinite(qty) ? qty : 0);
      });
      setOnHandByLoc(map);
    } catch {
      setOnHandByLoc({});
    }
  }

  const handlePick = (it) => {
    setPickedItem(it);
    setItemQuery(toStr(it?.item_code));
    setShowSuggest(false);
    refreshOnHandHints(it?.item_code);
  };

  const dCodeGuess = useMemo(() => {
    const guess = toStr(pickedItem?.item_code || dItemQuery).trim();
    return /^[\w-]{2,}$/.test(guess) ? guess : "";
  }, [pickedItem, dItemQuery]);

  useEffect(() => {
    if (!open) return;
    if (dCodeGuess) refreshOnHandHints(dCodeGuess);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, dCodeGuess]);

  // derived helpers
  const locNames = (locations || []).map((l) => toStr(l?.name)).filter(Boolean);
  const locNamesLC = locNames.map((n) => n.toLowerCase());

  const getAvailAt = (name) => {
    const n = toStr(name).trim().toLowerCase();
    if (!n) return null;
    for (const k of Object.keys(onHandByLoc)) {
      if (toStr(k).trim().toLowerCase() === n) {
        const v = Number(onHandByLoc[k]);
        return Number.isFinite(v) ? v : 0;
      }
    }
    return null;
  };

  // filter bin directory by typed location
  const binsForFromLoc = useMemo(() => {
    if (!toStr(fromLoc) || !bins.length) return bins;
    const needle = toStr(fromLoc).toLowerCase();
    const scoped = bins.filter((b) => toStr(b.location_name).toLowerCase() === needle);
    return scoped.length ? scoped : bins;
  }, [fromLoc, bins]);

  const binsForToLoc = useMemo(() => {
    if (!toStr(toLoc) || !bins.length) return bins;
    const needle = toStr(toLoc).toLowerCase();
    const scoped = bins.filter((b) => toStr(b.location_name).toLowerCase() === needle);
    return scoped.length ? scoped : bins;
  }, [toLoc, bins]);

  const softError = useMemo(() => {
    const qn = Number(qty);
    if (!toStr(pickedItem?.item_code) && !toStr(itemQuery).trim()) return "Item code is required";

    const t = String(type).toUpperCase();
    if (t === "TRANSFER") {
      if (!toStr(fromLoc).trim()) return "Transfer requires a From location";
      if (!toStr(toLoc).trim()) return "Transfer requires a To location";
      if (!Number.isFinite(qn) || qn <= 0) return "Transfer quantity must be > 0";
      const avail = getAvailAt(fromLoc);
      if (avail != null && qn > avail) return `Not enough stock at "${toLabel(fromLoc)}" (have ${avail}, need ${qn}).`;
    } else if (t === "RECEIVE") {
      if (!Number.isFinite(qn) || qn <= 0) return "Receive quantity must be > 0";
      if (!toStr(toLoc).trim()) return "Receive requires a destination";
    } else if (t === "ISSUE") {
      if (!Number.isFinite(qn) || qn <= 0) return "Issue quantity must be > 0";
      if (!toStr(fromLoc).trim()) return "Issue requires a source location";
      const avail = getAvailAt(fromLoc);
      if (avail != null && qn > avail) return `Not enough stock at "${toLabel(fromLoc)}" (have ${avail}, need ${qn}).`;
    } else {
      if (!Number.isFinite(qn)) return "Adjust quantity must be a number (can be negative or positive)";
      if (qn > 0 && !toStr(toLoc)) return "Positive adjust needs a target location";
      if (qn < 0 && !toStr(fromLoc)) return "Negative adjust needs a source location";
    }
    return "";
  }, [type, qty, itemQuery, pickedItem, fromLoc, toLoc, onHandByLoc]);

  const hardBlockReason = useMemo(() => {
    const qn = Number(qty);
    if (!Number.isFinite(qn) || qn <= 0) return "";
    const t = String(type).toUpperCase();
    const avail = getAvailAt(fromLoc);
    if (avail == null) return "";
    if (t === "ISSUE" || t === "TRANSFER") {
      if (qn > avail) return `Insufficient stock at "${toLabel(fromLoc)}" (have ${avail}, need ${qn}).`;
    }
    if (t === "ADJUST" && qn < 0 && Math.abs(qn) > avail) {
      return `Insufficient stock at "${toLabel(fromLoc)}" for negative adjustment (have ${avail}, need ${Math.abs(qn)}).`;
    }
    return "";
  }, [type, qty, fromLoc, onHandByLoc]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (hardBlockReason) return setError(hardBlockReason);
    if (softError) return setError(softError);

    setSubmitting(true);
    try {
      const payload = {
        type: String(type).toUpperCase(),
        item_code: toStr(pickedItem?.item_code || itemQuery).trim(),
        qty: Number(qty),
        from_loc: toStr(fromLoc) || undefined,
        to_loc: toStr(toLoc) || undefined,
        from_bin_code: toStr(fromBin) || undefined,
        to_bin_code: toStr(toBin) || undefined,
        reference: toStr(reference) || undefined,
        tx_date: txDate ? new Date(txDate).toISOString() : undefined,
      };

      const { data } = await api.post("/inventory/stock-movements", payload);
      onSaved?.(data);
      onClose();
    } catch (err) {
      const msg = err?.response?.data?.error || "Failed to save movement.";
      if (/Goods Received\s*\(GR\)\s*bin/i.test(msg) || err?.response?.status === 422) {
        setError(`${msg} Tip: make sure default GR/WASTE bins are seeded for this location.`);
      } else {
        setError(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    function onDocClick(e) {
      if (!suggBoxRef.current) return;
      if (!suggBoxRef.current.contains(e.target)) setShowSuggest(false);
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  useEffect(() => {
    if (type === "RECEIVE" && !toStr(toLoc) && goodsReceivedName) setToLoc(goodsReceivedName);
  }, [type, goodsReceivedName, toLoc]);

  if (!open) return null;

  const availAtFrom = getAvailAt(fromLoc);
  const qn = Number(qty);
  const willLeave =
    Number.isFinite(qn) && availAtFrom != null ? Math.max(0, Number(availAtFrom) - Math.max(0, qn)) : null;

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60">
      <div className="w-full max-w-2xl rounded-2xl bg-[#0f0f0f] border border-white/10 p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">New Stock Movement</h2>
          <button onClick={onClose} className="text-sm px-3 py-1 rounded bg-gray-800 hover:bg-gray-700">
            Close
          </button>
        </div>

        {(error || hardBlockReason || softError || binsLoadErr) && (
          <div className="mb-3 text-sm space-y-1">
            {binsLoadErr && <div className="text-amber-300">{toLabel(binsLoadErr)}</div>}
            {hardBlockReason ? (
              <div className="text-red-400">{toLabel(hardBlockReason)}</div>
            ) : error ? (
              <div className="text-red-400">{toLabel(error)}</div>
            ) : (
              <div className="text-amber-300">{toLabel(softError)}</div>
            )}
          </div>
        )}

        <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-gray-400 mb-1">Type</label>
            <select
              value={toStr(type)}
              onChange={(e) => setType(e.target.value)}
              className="w-full bg-[#1a1a1a] border border-white/10 rounded px-3 py-2 text-sm"
            >
              <option value="RECEIVE">Receive</option>
              <option value="ISSUE">Issue</option>
              <option value="ADJUST">Adjust</option>
              <option value="TRANSFER">Transfer</option>
            </select>
          </div>

          <div>
            <label className="block text-xs text-gray-400 mb-1">Date/Time</label>
            <input
              type="datetime-local"
              value={toStr(txDate)}
              onChange={(e) => setTxDate(e.target.value)}
              className="w-full bg-[#1a1a1a] border border-white/10 rounded px-3 py-2 text-sm"
            />
          </div>

          <div className="md:col-span-2" ref={suggBoxRef}>
            <label className="block text-xs text-gray-400 mb-1">Item (type code or description)</label>
            <div className="relative">
              <input
                value={toStr(itemQuery)}
                onChange={(e) => {
                  setItemQuery(e.target.value);
                  setPickedItem(null);
                  setShowSuggest(true);
                }}
                onFocus={() => setShowSuggest(true)}
                placeholder="e.g. MILK-1L"
                className="w-full bg-[#1a1a1a] border border-white/10 rounded px-3 py-2 text-sm"
              />
              {showSuggest && suggestions.length > 0 && (
                <div className="absolute z-10 mt-1 w-full max-h-60 overflow-auto rounded border border-white/10 bg-[#0e0e0e]">
                  {suggestions.map((it) => (
                    <button
                      key={toStr(it?.item_id ?? it?.item_code)}
                      type="button"
                      onClick={() => handlePick(it)}
                      className="w-full text-left px-3 py-2 hover:bg-[#1a1a1a] text-sm"
                    >
                      <div className="font-mono">{toLabel(it?.item_code)}</div>
                      <div className="text-xs text-gray-400">
                        {toLabel(it?.description)}
                        {it?.avg_cost != null && <span className="ml-2">· est £{Number(it.avg_cost).toFixed(2)}</span>}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            {pickedItem && <div className="mt-1 text-xs text-gray-400">{toLabel(pickedItem?.description)}</div>}
          </div>

          <div>
            <label className="block text-xs text-gray-400 mb-1">Quantity {type === "ADJUST" ? "(+/-)" : ""}</label>
            <input
              type="number"
              step="any"
              value={toStr(qty)}
              onChange={(e) => setQty(e.target.value)}
              className="w-full bg-[#1a1a1a] border border-white/10 rounded px-3 py-2 text-sm"
            />
          </div>

          <div>
            <label className="block text-xs text-gray-400 mb-1">From Location</label>
            <input
              list="locnames"
              value={toStr(fromLoc)}
              onChange={(e) => setFromLoc(e.target.value)}
              placeholder="Start typing or pick…"
              className="w-full bg-[#1a1a1a] border border-white/10 rounded px-3 py-2 text-sm"
            />
            {toStr(fromLoc) && !locNamesLC.includes(toStr(fromLoc).toLowerCase()) && (
              <div className="mt-1 text-xs text-amber-300">
                This will create a new location named “{toLabel(fromLoc)}”.
              </div>
            )}
            {pickedItem && toStr(fromLoc) && (
              <div className="mt-1 text-xs text-gray-400">
                Available: <span className="text-gray-200">{getAvailAt(fromLoc) ?? "—"}</span>
                {willLeave != null && Number.isFinite(willLeave) && Number(qn) > 0 && (
                  <span className="ml-2">
                    · Will leave <span className="text-gray-200">{willLeave}</span>
                  </span>
                )}
              </div>
            )}
          </div>

          <div>
            <label className="block text-xs text-gray-400 mb-1">To Location</label>
            <input
              list="locnames"
              value={toStr(toLoc)}
              onChange={(e) => setToLoc(e.target.value)}
              placeholder={type === "RECEIVE" && goodsReceivedName ? `Default: ${goodsReceivedName}` : "Start typing or pick…"}
              className="w-full bg-[#1a1a1a] border border-white/10 rounded px-3 py-2 text-sm"
            />
            {toStr(toLoc) && !locNamesLC.includes(toStr(toLoc).toLowerCase()) && (
              <div className="mt-1 text-xs text-amber-300">
                This will create a new location named “{toLabel(toLoc)}”.
              </div>
            )}
          </div>

          <datalist id="locnames">
            {(locations || []).flatMap((l) => {
              const n = toStr(l?.name).trim();
              return n ? [<option key={`${toStr(l?.location_id)}-${n}`} value={n} />] : [];
            })}
          </datalist>

          <div>
            <label className="block text-xs text-gray-400 mb-1">From Bin (optional)</label>
            <input
              list="frombinlist"
              value={toStr(fromBin)}
              onChange={(e) => setFromBin(e.target.value)}
              placeholder="Type or pick a bin code…"
              className="w-full bg-[#1a1a1a] border border-white/10 rounded px-3 py-2 text-sm"
            />
            <datalist id="frombinlist">
              {binsForFromLoc.map((b) => (
                <option key={`from-${toStr(b?.bin_id)}-${toStr(b?.bin_code)}`} value={toStr(b?.bin_code)}>
                  {toStr(b?.location_name) ? `${toStr(b.location_name)} · ${toLabel(b?.name)}` : toLabel(b?.name)}
                </option>
              ))}
            </datalist>
          </div>

          <div>
            <label className="block text-xs text-gray-400 mb-1">To Bin (optional)</label>
            <input
              list="tobinlist"
              value={toStr(toBin)}
              onChange={(e) => setToBin(e.target.value)}
              placeholder="Type or pick a bin code…"
              className="w-full bg-[#1a1a1a] border border-white/10 rounded px-3 py-2 text-sm"
            />
            <datalist id="tobinlist">
              {binsForToLoc.map((b) => (
                <option key={`to-${toStr(b?.bin_id)}-${toStr(b?.bin_code)}`} value={toStr(b?.bin_code)}>
                  {toStr(b?.location_name) ? `${toStr(b.location_name)} · ${toLabel(b?.name)}` : toLabel(b?.name)}
                </option>
              ))}
            </datalist>
          </div>

          <div className="md:col-span-2">
            <label className="block text-xs text-gray-400 mb-1">Reference</label>
            <input
              value={toStr(reference)}
              onChange={(e) => setReference(e.target.value)}
              placeholder="PO#, reason, etc."
              className="w-full bg-[#1a1a1a] border border-white/10 rounded px-3 py-2 text-sm"
            />
          </div>

          {pickedItem && Object.keys(onHandByLoc).length > 0 && (
            <div className="md:col-span-2 text-xs text-gray-300">
              <div className="mb-1">On-hand by location (hint):</div>
              <div className="flex flex-wrap gap-2">
                {Object.entries(onHandByLoc).map(([name, val]) => (
                  <span
                    key={toStr(name)}
                    className="px-2 py-1 rounded border border-white/10 bg-[#151515]"
                    title="Estimated from bin stock; backend enforces the true balance."
                  >
                    {toLabel(name)}: <span className="font-mono">{Number(val)}</span>
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="md:col-span-2 flex items-center justify-end gap-2 pt-1">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded bg-gray-800 hover:bg-gray-700 text-sm">
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || Boolean(hardBlockReason)}
              className="px-4 py-2 rounded bg-purple-700 hover:bg-purple-800 text-sm disabled:opacity-60 inline-flex items-center gap-2"
              title={toStr(hardBlockReason) || "Save movement"}
            >
              {submitting && <FaSyncAlt className="animate-spin" />}
              Save Movement
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ───────────────────────────── page ───────────────────────────── */
function BinsAndLocations() {
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [locations, setLocations] = useState([]);
  const [openNewMove, setOpenNewMove] = useState(false);

  const fetchLocations = async () => {
    setErr("");
    setLoading(true);

    const endpoints = [
      "/inventory/locations",
      "/inventory/location-directory",
      "/inventory/bins-locations/locations",
      "/inventory/stock-locations",
    ];

    let lastError = null;
    for (const url of endpoints) {
      try {
        const { data } = await api.get(url);
        const norm = normalizeLocations(data);
        // If endpoint exists but empty, still accept it
        setLocations(norm);
        lastError = null;
        break;
      } catch (e) {
        lastError = e;
        if (e?.response?.status === 404) continue;
        break;
      }
    }

    if (lastError) {
      setLocations([]);
      setErr(lastError?.response?.data?.error || lastError?.message || "Failed to load locations.");
    }

    setLoading(false);
  };

  useEffect(() => {
    fetchLocations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const grName = useMemo(() => {
    const byFlag = locations.find((l) => l.is_default_goods_received);
    if (byFlag) return byFlag.name;
    const byName = locations.find((l) => l.name.toLowerCase() === "goods received");
    return byName?.name || "";
  }, [locations]);

  return (
    <div className="min-h-screen bg-[#0b0f19] text-white p-6">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold">Bins & Locations</h1>
          <p className="text-sm text-white/60 mt-1">
            Manage stock locations and create movements (Receive / Issue / Adjust / Transfer).
          </p>
          {grName && <p className="text-xs text-white/40 mt-1">Default GR location: {grName}</p>}
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => fetchLocations()}
            className="px-3 py-2 rounded bg-white/10 hover:bg-white/15 border border-white/10 text-sm"
            type="button"
          >
            Refresh
          </button>
          <button
            onClick={() => setOpenNewMove(true)}
            className="px-3 py-2 rounded bg-purple-700 hover:bg-purple-800 text-sm"
            type="button"
          >
            New Stock Movement
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-white/60">Loading locations…</div>
      ) : err ? (
        <div className="text-sm text-red-300 border border-red-500/30 bg-red-900/20 rounded-xl p-4">{err}</div>
      ) : (
        <div className="rounded-2xl border border-white/10 bg-white/5 overflow-hidden">
          <div className="px-4 py-3 border-b border-white/10 text-sm font-semibold">Locations</div>
          {locations.length === 0 ? (
            <div className="p-4 text-sm text-white/60">No locations found.</div>
          ) : (
            <div className="divide-y divide-white/10">
              {locations.map((l) => (
                <div key={toStr(l.location_id) + l.name} className="px-4 py-3 flex items-center justify-between gap-3">
                  <div>
                    <div className="font-medium">{l.name}</div>
                    <div className="text-xs text-white/50">
                      {l.is_default_goods_received ? "Default GR" : ""}
                      {l.is_default_goods_received && l.is_default_wastage ? " · " : ""}
                      {l.is_default_wastage ? "Default Waste" : ""}
                    </div>
                  </div>
                  <div className="text-xs text-white/40">{l.location_id != null ? `#${l.location_id}` : ""}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <NewMovementModal
        open={openNewMove}
        onClose={() => setOpenNewMove(false)}
        locations={locations}
        onSaved={() => fetchLocations()}
      />
    </div>
  );
}

// ✅ Export BOTH ways (prevents future Vercel import mistakes)
export { BinsAndLocations };
export default BinsAndLocations;
